import { Section } from "../tools/section";
import { Swiper } from "swiper";
import { Pagination, EffectFade } from "swiper/modules";
import { ScrollTrigger } from "../animations/core/gsap";
import { CLASS_VIDEO_ACTIVE, CLASS_MUTED } from "../tools/constants/classes";
import { t } from "@process-creative/slate-theme-tools";

import { animateTextInward, animateVideoText, animateTextOutward } 
  from "../animations/sections/video-animation-slides";

const SELECTOR_SECTION = "[data-video-animation-slides]";
const SELECTOR_SLIDER = "[data-slide-slider]";
const SELECTOR_SLIDE = "[data-slide-slide]";
const SELECTOR_PAGINATION = "[data-slide-pagination]";
const SELECTOR_PROGRESSBAR = "[data-slide-progressbar]";

const SELECTOR_VIDEO_ELEMENT = "[data-slide-video-element]";
const SELECTOR_VIDEO_MUTE_BTN = "[data-slide-mute-unmute]";

const SLIDE_TYPES = {
  TEXT_INWARD: "text_inward",
  TEXT_OUTWARD: "text_outward",
  VIDEO_TEXT: "video_text",
} as const;

export class VideoAnimationSlides extends Section {
  private swiper!: any;
  private slides!: HTMLElement[];
  private currentVideoTimeout: number | null = null;
  private isVideoPlaying: boolean = false;

  queryChildren(container: HTMLElement) {
    this.container = container;
    this.slides = Array.from(
      this.container.querySelectorAll<HTMLElement>(SELECTOR_SLIDE)
    );
  }

  attachEvents() {
    this.initSwiper();
    this.initialiseAnimations();

    this.container.addEventListener("slideNext", () => {
      const currentSlide = this.slides[this.swiper.activeIndex];
      const videoEl = currentSlide?.querySelector<HTMLVideoElement>(SELECTOR_VIDEO_ELEMENT);
      
      if (videoEl && this.isVideoPlaying) {
        if (videoEl.ended || videoEl.currentTime >= videoEl.duration) {
          this.isVideoPlaying = false;
          this.swiper.slideNext();
        }
      } else {
        this.swiper.slideNext();
      }
    });
  }

  detatchEvents() {
    if (this.currentVideoTimeout) {
      clearTimeout(this.currentVideoTimeout);
      this.currentVideoTimeout = null;
    }
    
    this.isVideoPlaying = false;
    this.swiper?.destroy?.(true, true);
  }

  private initSwiper() {
    const el = this.container.querySelector(SELECTOR_SLIDER) as HTMLElement;
    if (!el) return;

    // Swiper options
    const swiperOptions: any = {
      modules: [Pagination, EffectFade],
      slidesPerView: 1,
      speed: 600,
      effect: "fade",
      allowTouchMove: true,
      loop: false,
      on: {
        slideChange: () => {
          if (this.currentVideoTimeout) {
            clearTimeout(this.currentVideoTimeout);
            this.currentVideoTimeout = null;
          }

          const currentSlide = this.slides[this.swiper.activeIndex];
          const type = currentSlide.dataset.slideType;
          const isLastSlide = this.swiper.activeIndex === this.slides.length - 1;
        
          let duration = 0;
        
          if (type === SLIDE_TYPES.TEXT_INWARD || type === SLIDE_TYPES.TEXT_OUTWARD) {
            const tl = type === SLIDE_TYPES.TEXT_INWARD
              ? animateTextInward(currentSlide, isLastSlide)
              : animateTextOutward(currentSlide, isLastSlide);
            duration = tl.duration() * 1000;
            this.runProgressBar(duration);
            
            if (!isLastSlide) {
              this.currentVideoTimeout = window.setTimeout(() => {
                this.swiper.slideNext();
              }, duration);
            }
          }
        
          if (type === SLIDE_TYPES.VIDEO_TEXT) {
            const videoEl = currentSlide.querySelector<HTMLVideoElement>(SELECTOR_VIDEO_ELEMENT);
            if (videoEl) {
              this.setupVideoSlide(videoEl, currentSlide, isLastSlide);
            }
          }
        },
        slideChangeTransitionStart: () => {
          this.updateSliderClassForVideo();
          
          if (this.currentVideoTimeout) {
            clearTimeout(this.currentVideoTimeout);
            this.currentVideoTimeout = null;
          }
          
          this.isVideoPlaying = false;

          this.slides.forEach((slide, index) => {
            if (index !== this.swiper.activeIndex) {
              const videoEl = slide.querySelector<HTMLVideoElement>(SELECTOR_VIDEO_ELEMENT);
              if (videoEl) {
                videoEl.pause();
                videoEl.currentTime = 0;
                videoEl.onended = null;
                videoEl.onplay = null;
                videoEl.onpause = null;
                videoEl.onloadedmetadata = null;
                videoEl.muted = true;
              }
            }
          });
          
          this.resetAllProgressBars();
        }
      }
    };

    // Only enable pagination if more than one slide
    if (this.slides.length > 1) {
      swiperOptions.pagination = {
        el: this.container.querySelector(SELECTOR_PAGINATION),
        clickable: true,
        renderBullet: function (index: number, className: string) {
          return `
            <span class="c-video-animation-slides__pagination-bullet ${className}">
              <span class="c-video-animation-slides__pagination-number">${index + 1}</span>
              <span class="c-video-animation-slides__progress">
                <span class="c-video-animation-slides__progress-track"></span>
                <span class="c-video-animation-slides__progress-bar" data-slide-progressbar></span>
              </span>
            </span>
          `;
        }
      };
    } else {
      swiperOptions.pagination = false;
    }

    this.swiper = new Swiper(el, swiperOptions);
  }

  initialiseAnimations() {
    ScrollTrigger.create({
      trigger: this.container,
      start: "top 80%",
      once: true,
      onEnter: () => {
        this.swiper.slideTo(0); // reset
        this.swiper.emit("slideChange"); // manually trigger first anim
      }
    });
  }

  private runProgressBar(duration: number) {
    this.resetAllProgressBars();
    
    const bullets = this.container.querySelectorAll('.swiper-pagination-bullet');
    bullets.forEach((bullet, i) => {
      const progress = bullet.querySelector<HTMLElement>(SELECTOR_PROGRESSBAR);
      if (!progress) return;
      
      if (i === this.swiper.activeIndex) {
        requestAnimationFrame(() => {
          progress.style.transitionDuration = `${duration}ms`;
          progress.style.width = '100%';
        });
      }
    });
  }

  private resetAllProgressBars() {
    const bullets = this.container.querySelectorAll('.swiper-pagination-bullet');
    bullets.forEach((bullet) => {
      const progress = bullet.querySelector<HTMLElement>(SELECTOR_PROGRESSBAR);
      if (!progress) return;
      
      progress.style.transitionDuration = '0ms';
      progress.style.width = '0%';
    });
  }

  private updateSliderClassForVideo() {
    const currentSlide = this.slides[this.swiper.activeIndex];
    const type = currentSlide.dataset.slideType;
    const sliderEl = this.container.querySelector(SELECTOR_SLIDER);
    if (!sliderEl) return;

    if (type === SLIDE_TYPES.VIDEO_TEXT) {
      sliderEl.classList.add(CLASS_VIDEO_ACTIVE);
    } else {
      sliderEl.classList.remove(CLASS_VIDEO_ACTIVE);
    }
  }

  private setupVideoSlide(videoEl: HTMLVideoElement, currentSlide: HTMLElement, isLastSlide: boolean) {
    videoEl.currentTime = 0;
    this.isVideoPlaying = true;
    
    videoEl.onended = null;
    videoEl.onplay = null;
    videoEl.onloadedmetadata = null;
    videoEl.onpause = null;
    
    const handleVideoSetup = () => {
      const duration = videoEl.duration * 1000;
      
      animateVideoText(currentSlide, isLastSlide);
      
      videoEl.onplay = () => {
        this.isVideoPlaying = true;
        this.runProgressBar(duration);
      };
      
      setTimeout(() => {
        const animationOnended = videoEl.onended;
        videoEl.onended = (event) => {
          this.isVideoPlaying = false;
          
          if (animationOnended && animationOnended !== videoEl.onended) {
            animationOnended.call(videoEl, event);
          }
        };
      }, 150);
      
      const playPromise = videoEl.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(() => {
          this.isVideoPlaying = false;
          this.runProgressBar(duration);
        });
      }
    };
    
    if (videoEl.readyState >= 1) {
      handleVideoSetup();
    } else {
      videoEl.onloadedmetadata = handleVideoSetup;
      videoEl.load();
    }
    
    this.attachMuteUnmuteHandler(currentSlide);
  }

  private attachMuteUnmuteHandler(slide: HTMLElement) {
    const videoEl = slide.querySelector<HTMLVideoElement>(SELECTOR_VIDEO_ELEMENT);
    const muteBtn = slide.querySelector<HTMLElement>(SELECTOR_VIDEO_MUTE_BTN);
    if (!videoEl || !muteBtn) return;
  
    // Prevent duplicate bindings
    if ((muteBtn as any)._listenerAttached) return;
    (muteBtn as any)._listenerAttached = true;
  
    // Ensure inline playback compatibility for mobile browsers
    videoEl.playsInline = true;
    videoEl.setAttribute('playsinline', '');
    videoEl.setAttribute('webkit-playsinline', '');
  
    const updateButtonState = () => {
      const isMuted = videoEl.muted || videoEl.volume === 0;
      muteBtn.classList.toggle(CLASS_MUTED, isMuted);
      muteBtn.setAttribute('aria-pressed', String(isMuted));
      muteBtn.setAttribute('aria-label', isMuted ? t('general.accessibility.unmute_video') : t('general.accessibility.mute_video'));
    };
  
    // Initialize UI state
    updateButtonState();
  
    muteBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
  
      videoEl.muted = !videoEl.muted;
  
      // On unmute, ensure volume is restored and playback continues
      if (!videoEl.muted) {
        videoEl.volume = 1;
        const playPromise = videoEl.play?.();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.catch(() => {
            // Ignore autoplay restriction errors
          });
        }
      }
  
      updateButtonState();
    });
  
    // Sync button state with external changes to mute/volume
    videoEl.addEventListener('volumechange', updateButtonState);
  }
}

Section.register(SELECTOR_SECTION, VideoAnimationSlides);